import React, {useState, useEffect, useRef, useCallback} from "react";
import Icon from "./Icon";
import AiMessage from "./AiMessage";
import AiBottomSearchBar from "./AiBottomSearchBar";
import {onboardingGateMessage} from "./Constants";
import {formatPrice} from "./Helpers";
import "../../styles/ai/ai-chatbox.css";
import "../../styles/ai/ai-chips.css";
import "../../styles/ai/ai-search.css";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GMAPS_LOAD_TIMEOUT_MS = 15000;

function loadGoogleMaps() {
    if (typeof window.google?.maps?.Map === "function") return Promise.resolve();

    if (document.getElementById("gmaps-sdk")) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const t = setInterval(() => {
                if (typeof window.google?.maps?.Map === "function") {
                    clearInterval(t);
                    resolve();
                } else if (Date.now() - startTime > GMAPS_LOAD_TIMEOUT_MS) {
                    clearInterval(t);
                    reject(new Error("Google Maps SDK failed to load within 15s — check network or ad blocker"));
                }
            }, 100);
        });
    }

    return new Promise((resolve, reject) => {
        if (!GMAPS_KEY) {
            reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set in .env"));
            return;
        }
        const script = document.createElement("script");
        script.id = "gmaps-sdk";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places,geometry,marker&v=weekly`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Google Maps SDK"));
        document.head.appendChild(script);
    });
}

/**
 * Main AI chat panel: renders the conversation, resolves and displays
 * routes/nearby-place/road-corridor searches on an embedded Google Map,
 * and drives the bottom search bar and property-map interactions.
 */
export default function AiChatBox({
                                      chat,
                                      loading = false,
                                      onSend,
                                      onEditMessage,
                                      onRetry,
                                      onCopy,
                                      userPosition,
                                      compareList = [],
                                      onCompare,
                                      onApplyRelaxation,
                                      onLoadMore,
                                  }) {
    const [input, setInput] = useState("");
    const [mapProperties, setMapProperties] = useState(null);
    const [mapsReady, setMapsReady] = useState(
        typeof window.google?.maps?.Map === "function"
    );
    const [activeListItem, setActiveListItem] = useState(null);
    const [roadGeometry, setRoadGeometry] = useState(null);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const mapContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const autocompleteRef = useRef(null);
    const roadOverlaysRef = useRef([]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    }, [chat?.messages?.length, loading]);

    useEffect(() => {
        setTimeout(() => textareaRef.current?.focus(), 60);
    }, [chat?.id]);

    useEffect(() => {
        setMapProperties(null);
        setRoadGeometry(null);
    }, [chat?.id]);

    useEffect(() => {
        if (mapsReady) return;
        loadGoogleMaps()
            .then(() => setMapsReady(true))
            .catch((err) => console.error("[AiChatBox] Maps SDK:", err.message));
    }, [mapsReady]);

    // Route/distance queries ("distance from X to Y") don't open a map panel
    // here at all -- AiMessage.js's own InlineRouteMap renders the map, mode
    // tabs, and distance/time stats directly under the user's message,
    // which is the sole route-view experience. This file used to ALSO
    // auto-open a separate "Route & Directions" side panel for the same
    // query via its own independent geocoding -- found live, that second
    // attempt failed ("Could not find one of the locations") on a bare
    // "hsr"/"marathahalli" (no city context) even when InlineRouteMap and
    // the chat's own text answer both succeeded for the identical query, a
    // confusing and less accurate duplicate. Removed entirely, along with
    // the route-specific state/rendering (routeRequest/routeMode/
    // routeResult/routeError/routeLoading, renderRoute, the "Show Route on
    // Map" button in AiMessage.js) that only ever existed to drive it.

    useEffect(() => {
        if (!chat?.messages?.length) return;
        const msgs = chat.messages;
        const lastAi = [...msgs].reverse().find((m) => m.role === "ai");
        if (lastAi?.isRoadSearch && lastAi?.roadPolyline) {
            setRoadGeometry({
                roadName: lastAi.roadName,
                roadPolyline: lastAi.roadPolyline,
                bufferKm: lastAi.bufferKm,
            });
            setMapProperties(lastAi.properties || []);
        }
    }, [chat?.messages]);

    const destroyMap = useCallback(() => {
        autocompleteRef.current = null;
        markersRef.current.forEach((m) => m?.setMap(null));
        markersRef.current = [];
        roadOverlaysRef.current.forEach((o) => o?.setMap(null));
        roadOverlaysRef.current = [];
        infoWindowRef.current?.close();
        if (mapContainerRef.current) mapContainerRef.current.innerHTML = "";
        mapInstanceRef.current = null;
        setActiveListItem(null);
    }, []);

    const focusMarker = useCallback((index) => {
        const map = mapInstanceRef.current;
        const marker = markersRef.current[index];
        const prop = mapProperties?.[index];
        if (!map || !marker || !prop) return;

        map.panTo(marker.getPosition());
        map.setZoom(15);
        const pos = marker.getPosition();

        const mUrl = pos
            ? `https://www.google.com/maps/search/?api=1&query=${pos.lat()},${pos.lng()}`
            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((prop.location ?? "") + ", India")}`;

        infoWindowRef.current?.setContent(
            `<div style="font-family:sans-serif;min-width:210px;padding:4px 2px;">
         <strong style="font-size:14px;display:block;margin-bottom:4px;">${prop.title ?? ""}</strong>
         <span style="color:#6b7280;font-size:12px;">${prop.location ?? ""}</span><br/>
         <span style="color:#2563eb;font-weight:700;font-size:14px;">${formatPrice(prop.price)}</span>
         <a href="${mUrl}" target="_blank" rel="noopener noreferrer"
            style="display:inline-flex;align-items:center;gap:4px;margin-top:8px;
                   color:#2563eb;font-size:12px;font-weight:600;text-decoration:none;
                   background:#eff6ff;padding:4px 12px;border-radius:20px;border:1px solid #bfdbfe;">
           🗺 Open in Google Maps →
         </a>
       </div>`
        );
        infoWindowRef.current?.open(map, marker);
        setActiveListItem(index);
    }, [mapProperties]);

    useEffect(() => {
        if (mapProperties === null) {
            destroyMap();
            return;
        }
        if (!mapsReady) return;

        destroyMap();
        if (!mapContainerRef.current) return;

        const G = window.google.maps;

        const parsedCoords = (mapProperties || []).map((p) => {
            const lat = parseFloat(p.latitude ?? p.lat);
            const lng = parseFloat(p.longitude ?? p.lng ?? p.lon);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0 ? {lat, lng} : null;
        });

        const validCoords = parsedCoords.filter(Boolean);

        let center = {lat: 12.9716, lng: 77.5946};
        if (validCoords.length > 0) {
            center = {
                lat: validCoords.reduce((s, c) => s + c.lat, 0) / validCoords.length,
                lng: validCoords.reduce((s, c) => s + c.lng, 0) / validCoords.length,
            };
        }

        const map = new G.Map(mapContainerRef.current, {
            center,
            zoom: validCoords.length === 1 ? 15 : 12,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
        });
        mapInstanceRef.current = map;

        map.addListener("click", (e) => {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${e.latLng.lat()},${e.latLng.lng()}`,
                "_blank", "noopener,noreferrer"
            );
        });

        const infoWindow = new G.InfoWindow();
        infoWindowRef.current = infoWindow;

        const makeIcon = (colour) => ({
            path: G.SymbolPath.CIRCLE,
            fillColor: colour,
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            scale: 15,
        });

        const bounds = new G.LatLngBounds();
        markersRef.current = (mapProperties || []).map((prop, i) => {
            const coords = parsedCoords[i];

            if (!coords) return null;

            const marker = new G.Marker({
                position: coords,
                map,
                label: {text: String(i + 1), color: "#fff", fontWeight: "700", fontSize: "12px"},
                title: prop.title,
                icon: makeIcon("#2563eb"),
            });

            marker.addListener("click", () => {
                const mPos = marker.getPosition();
                const mUrl = mPos
                    ? `https://www.google.com/maps/search/?api=1&query=${mPos.lat()},${mPos.lng()}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((prop.location ?? "") + ", India")}`;

                infoWindow.setContent(
                    `<div style="font-family:sans-serif;min-width:210px;padding:4px 2px;">
             <strong style="font-size:14px;display:block;margin-bottom:4px;">${prop.title ?? ""}</strong>
             <span style="color:#6b7280;font-size:12px;">${prop.location ?? ""}</span><br/>
             <span style="color:#2563eb;font-weight:700;font-size:14px;">${formatPrice(prop.price)}</span>
             <a href="${mUrl}" target="_blank" rel="noopener noreferrer"
                style="display:inline-flex;align-items:center;gap:4px;margin-top:8px;
                       color:#2563eb;font-size:12px;font-weight:600;text-decoration:none;
                       background:#eff6ff;padding:4px 12px;border-radius:20px;border:1px solid #bfdbfe;">
               🗺 Open in Google Maps →
             </a>
           </div>`
                );
                infoWindow.open(map, marker);
                setActiveListItem(i);
            });

            bounds.extend(coords);
            return marker;
        }).filter(Boolean);

        // Road-corridor overlay: draw the resolved polyline, and only for a
        // genuine buffer/corridor search (bufferKm present) also draw a
        // translucent buffer band (sampled circles) showing the proximity
        // zone behind the results. Plain "show X on map" lookups have no
        // bufferKm and get just the road line — drawing buffer circles for
        // those defaulted to 1km and stacked into a solid orange flood.
        roadOverlaysRef.current.forEach((o) => o?.setMap(null));
        roadOverlaysRef.current = [];

        if (roadGeometry?.roadPolyline && G.geometry?.encoding) {
            try {
                const path = G.geometry.encoding.decodePath(roadGeometry.roadPolyline);
                if (path.length > 1) {
                    const roadLine = new G.Polyline({
                        path,
                        strokeColor: "#1a73e8",
                        strokeWeight: 4,
                        strokeOpacity: 0.95,
                        zIndex: 5,
                    });
                    roadLine.setMap(map);
                    roadOverlaysRef.current.push(roadLine);

                    if (roadGeometry.bufferKm) {
                        const bufferMeters = roadGeometry.bufferKm * 1000;

                        // Sample by real distance along the road, not by
                        // polyline-vertex index — index-based sampling
                        // clusters circles wherever the road has lots of
                        // vertices (e.g. wiggly stretches), and with a
                        // several-km radius that overlap stacks even low
                        // fill opacity into solid orange. Spacing samples
                        // ~1.5x the buffer radius apart keeps the band
                        // gap-free without heavy overlap.
                        const stepMeters = Math.max(bufferMeters * 1.5, 400);
                        let distSinceLastSample = Infinity;

                        for (let i = 0; i < path.length; i++) {
                            if (i > 0 && G.geometry?.spherical) {
                                distSinceLastSample += G.geometry.spherical.computeDistanceBetween(
                                    path[i - 1], path[i]);
                            }
                            if (distSinceLastSample < stepMeters) continue;
                            distSinceLastSample = 0;

                            const circle = new G.Circle({
                                center: path[i],
                                radius: bufferMeters,
                                strokeOpacity: 0,
                                strokeWeight: 0,
                                fillColor: "#1a73e8",
                                fillOpacity: 0.035,
                                clickable: false,
                                zIndex: 1,
                            });
                            circle.setMap(map);
                            roadOverlaysRef.current.push(circle);
                        }
                    }

                    path.forEach((pt) => bounds.extend(pt));
                }
            } catch (e) {
                console.warn("[AiChatBox] Failed to draw road polyline:", e.message);
            }
        }

        if (validCoords.length > 1 || roadGeometry?.roadPolyline) map.fitBounds(bounds, 60);

        if (searchInputRef.current) {
            const ac = new G.places.Autocomplete(searchInputRef.current, {
                fields: ["geometry", "name", "formatted_address"],
            });
            ac.addListener("place_changed", () => {
                const place = ac.getPlace();
                if (!place.geometry?.location) return;
                map.panTo(place.geometry.location);
                map.setZoom(15);
                new G.Marker({
                    position: place.geometry.location,
                    map,
                    title: place.name,
                    icon: {
                        path: G.SymbolPath.BACKWARD_CLOSED_ARROW,
                        fillColor: "#ef4444",
                        fillOpacity: 1,
                        strokeColor: "#fff",
                        strokeWeight: 1.5,
                        scale: 7
                    },
                });
                infoWindow.setContent(
                    `<div style="font-family:sans-serif;padding:4px 2px;">
             <strong>${place.name ?? ""}</strong><br/>
             <span style="color:#6b7280;font-size:12px;">${place.formatted_address ?? ""}</span>
           </div>`
                );
                infoWindow.setPosition(place.geometry.location);
                infoWindow.open(map);
            });
            autocompleteRef.current = ac;
        }

        setTimeout(() => G.event.trigger(map, "resize"), 420);
        return destroyMap;
    }, [mapProperties, mapsReady, destroyMap, roadGeometry]);

    const handleSend = useCallback((t) => {
        const text = (t || input).trim();
        if (!text || loading) return;
        setInput("");
        if (textareaRef.current) textareaRef.current.style.height = "auto";
        onSend?.({
            question: text,
            userLatitude: userPosition?.latitude ?? null,
            userLongitude: userPosition?.longitude ?? null,
            userLocationName: userPosition?.locationName ?? null,
        });
    }, [input, loading, onSend, userPosition]);

    const handleFollowUp = useCallback((s, opts) => {
        if (!loading) onSend?.({
            question: s,
            userLatitude: userPosition?.latitude ?? null,
            userLongitude: userPosition?.longitude ?? null,
            userLocationName: userPosition?.locationName ?? null,
            ...opts,
        });
    }, [loading, onSend, userPosition]);

    const handleMapView = useCallback((properties) => {
        setMapProperties((prev) => {
            if (prev === properties) return null;
            setRoadGeometry(null);
            return properties;
        });
    }, []);

    const handleRoadMapView = useCallback((properties, geometry) => {
        setRoadGeometry(geometry || null);
        setMapProperties(properties || []);
    }, []);

    const closeMap = useCallback(() => {
        setMapProperties(null);
        setRoadGeometry(null);
    }, []);

    const isMapOpen = mapProperties !== null;
    const hasProperties = (mapProperties?.length ?? 0) > 0;
    const isRoadActive = !!roadGeometry?.roadPolyline;

    if (!chat) {
        return (
            <div className="ai-content">
                <div className="chat-empty-state">
                    <div className="chat-empty-icon"><Icon name="sparkle" size={28}/></div>
                    <h2>AI Property Agent</h2>
                    <p>Search, compare, and discover properties. Ask anything to get started.</p>
                </div>
            </div>
        );
    }

    const messages = chat.messages || [];
    // A new chat opens on the guided-search greeting; sending anything turns it into a real message (AiSearchPage).
    const shownMessages = messages.length === 0 && !loading ? [onboardingGateMessage()] : messages;

    return (
        <div className="ai-content">
            <div className="ai-split-wrapper">

                <div className={`ai-chat-panel ${isMapOpen ? "with-map" : ""}`}>
                    <div className="chat-messages-area" role="log" aria-live="polite">
                        <div className="chat-messages-container">
                            {shownMessages.map((msg, i) => (
                                <AiMessage
                                    key={msg.id || `msg-${i}`}
                                    msg={msg}
                                    chatId={chat.id}
                                    index={i}
                                    onEdit={onEditMessage}
                                    onRetry={onRetry}
                                    onCopy={onCopy}
                                    onFollowUp={handleFollowUp}
                                    onMapView={handleMapView}
                                    onRoadMapView={handleRoadMapView}
                                    isMapOpen={isMapOpen}
                                    userPosition={userPosition}
                                    prevMessages={shownMessages.slice(0, i)}
                                    compareList={compareList}
                                    onCompare={onCompare}
                                    onApplyRelaxation={onApplyRelaxation}
                                    onLoadMore={onLoadMore}
                                    loading={loading}
                                    // A question already answered must not send a second answer from its old chips.
                                    answered={msg.role === "ai" && shownMessages.slice(i + 1).some((m) => m.role === "user")}
                                />
                            ))}
                            {loading && (
                                <div className="message-row ai" role="status" aria-label="AI is thinking">
                                    <div className="typing-indicator">
                                        <div className="typing-dot"/>
                                        <div className="typing-dot"/>
                                        <div className="typing-dot"/>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef}/>
                        </div>
                    </div>

                    <div className="chat-input-bar">
                        <AiBottomSearchBar onSend={handleSend} busy={loading}/>
                    </div>
                </div>

                {isMapOpen && (
                    <div className="ai-map-panel">
                        <div className="ai-map-header">
                  <span className="ai-map-title">
                    {isRoadActive ? `🛣️ ${roadGeometry.roadName || "Road"}${roadGeometry.bufferKm ? " Corridor" : ""}`
                        : "📍 Properties on Map"}
                  </span>
                            {hasProperties && (
                                <span className="ai-map-count">
                        {mapProperties.length} properties
                                    {isRoadActive && roadGeometry?.bufferKm
                                        ? ` within ±${roadGeometry.bufferKm} km`
                                        : ""}
                      </span>
                            )}
                            <button className="ai-map-close" onClick={closeMap} aria-label="Close map">
                                ✕ Close
                            </button>
                        </div>

                        <div className="ai-map-search-bar">
                            <svg className="ai-map-search-icon" width="15" height="15" viewBox="0 0 24 24"
                                 fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <circle cx="11" cy="11" r="7"/>
                                <line x1="16.5" y1="16.5" x2="22" y2="22"/>
                            </svg>
                            <input ref={searchInputRef} className="ai-map-search-input"
                                   type="text" placeholder="Search any location on map…"
                                   aria-label="Search location on map"/>
                        </div>

                        <div className="ai-map-frame-wrap" ref={mapContainerRef}/>

                        {hasProperties && (
                            <div className="ai-map-list" role="list">
                                {mapProperties.map((p, i) => (
                                    <div key={p.id || i}
                                         className={`ai-map-item${activeListItem === i ? " active" : ""}`}
                                         onClick={() => focusMarker(i)}
                                         role="button" tabIndex={0}
                                         onKeyDown={(e) => e.key === "Enter" && focusMarker(i)}
                                         aria-label={`${p.title} - ${p.location}`}>
                                        <div className="ai-map-item-marker">{i + 1}</div>
                                        <div className="ai-map-item-info">
                                            <div className="ai-map-item-title">{p.title}</div>
                                            <div className="ai-map-item-loc">{p.location || "—"}</div>
                                        </div>
                                        <div className="ai-map-item-price">{formatPrice(p.price)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}